import { useId } from "react";
import { motion } from "framer-motion";
import { EASE } from "@/lib/motion";

/**
 * Maestro's product visual: independent musical phrases resolving into one
 * composition. Offset ribbons suggest rhythm, harmony, and arrangement
 * without relying on literal notes, instruments, or a conductor's baton.
 */
export default function MaestroGraphic({ className = "" }) {
    const uid = useId().replace(/:/g, "");
    const scoreId = `${uid}-maestro-score`;
    const glowId = `${uid}-maestro-glow`;
    const haloId = `${uid}-maestro-halo`;
    const phrases = [
        { y: 50, bend: 34, delay: 0 },
        { y: 80, bend: 17, delay: 0.1 },
        { y: 110, bend: 0, delay: 0.2 },
        { y: 140, bend: -17, delay: 0.3 },
        { y: 170, bend: -34, delay: 0.4 },
    ];

    return (
        <svg
            viewBox="0 0 600 220"
            className={className}
            aria-hidden="true"
            preserveAspectRatio="xMidYMid meet"
        >
            <defs>
                <linearGradient id={scoreId} x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#FFB84D" />
                    <stop offset="45%" stopColor="#FF6B4A" />
                    <stop offset="100%" stopColor="#FF4DA6" />
                </linearGradient>
                <radialGradient id={glowId} cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#FF8A00" stopOpacity="0.2" />
                    <stop offset="58%" stopColor="#FF4DA6" stopOpacity="0.08" />
                    <stop offset="100%" stopColor="#8A2BE2" stopOpacity="0" />
                </radialGradient>
                <linearGradient id={haloId} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#FFB84D" />
                    <stop offset="52%" stopColor="#FF8A00" />
                    <stop offset="100%" stopColor="#FF4DA6" />
                </linearGradient>
            </defs>

            <ellipse cx="300" cy="110" rx="168" ry="104" fill={`url(#${glowId})`} />

            {phrases.map(({ y, bend, delay }, i) => (
                <motion.path
                    key={y}
                    d={`M 92 ${y}
                        C 174 ${y - bend},
                          224 ${110 + bend * 0.42},
                          300 110
                        C 376 ${110 - bend * 0.42},
                          426 ${y + bend},
                          508 ${y}`}
                    fill="none"
                    stroke={`url(#${scoreId})`}
                    strokeWidth={i === 2 ? 3 : 1.7}
                    strokeLinecap="round"
                    opacity={i === 2 ? 0.95 : 0.72}
                    initial={{ pathLength: 0, opacity: 0 }}
                    whileInView={{ pathLength: 1, opacity: i === 2 ? 0.95 : 0.72 }}
                    viewport={{ once: true }}
                    transition={{ duration: 2.1, delay, ease: EASE }}
                />
            ))}

            {[0, 1, 2].map((i) => (
                <motion.ellipse
                    key={i}
                    cx="300"
                    cy="110"
                    rx={34 + i * 25}
                    ry={34 + i * 15}
                    fill="none"
                    stroke={`url(#${haloId})`}
                    strokeWidth="1"
                    strokeDasharray={i === 1 ? "3 7" : undefined}
                    opacity={0.58 - i * 0.14}
                    initial={{ scale: 0.72, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 0.58 - i * 0.14 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.5, delay: 0.42 + i * 0.12, ease: EASE }}
                    style={{ transformOrigin: "300px 110px" }}
                />
            ))}

            <motion.path
                d="M 300 82
                   C 304 104 318 108 338 110
                   C 318 112 304 116 300 138
                   C 296 116 282 112 262 110
                   C 282 108 296 104 300 82 Z"
                fill={`url(#${haloId})`}
                initial={{ scale: 0.35, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.35, delay: 0.65, ease: EASE }}
                style={{ transformOrigin: "300px 110px" }}
            />

            {[
                [92, 50], [92, 110], [92, 170],
                [508, 50], [508, 110], [508, 170],
            ].map(([cx, cy], i) => (
                <motion.circle
                    key={`${cx}-${cy}`}
                    cx={cx}
                    cy={cy}
                    r={i % 3 === 1 ? 3.5 : 2.5}
                    fill={cx < 300 ? "#FFB84D" : "#FF4DA6"}
                    initial={{ scale: 0, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 0.9 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.28 + i * 0.07, ease: EASE }}
                />
            ))}
        </svg>
    );
}
